import React, { useState, useRef, useEffect } from 'react';
import MainLayout from '../../components/MainLayout';
import { sendAgentMessageStream } from '../../services/api';
import DynamicChart from '../../components/DynamicChart';
import TableRenderer from '../../components/TableRenderer';
import { getStoredUser } from '../../services/authService';
import './AIAnalysis.css';

function AIAnalysis() {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: '안녕하세요! AI 분석 도우미입니다. 사출 설비 데이터, 생산 현황, 알람 분석 등에 대해 질문해주세요.',
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [streamingText, setStreamingText] = useState('');
  const [statusText, setStatusText] = useState('');
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // 메시지가 추가될 때마다 스크롤을 아래로
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // textarea 자동 높이 조절
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + 'px';
    }
  }, [inputValue]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputValue,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const messageToSend = inputValue;
    setInputValue('');
    setIsLoading(true);
    setError(null);
    setStreamingText('');
    setStatusText('');

    try {
      // 멀티 에이전트 스트리밍 API 호출
      const conversationHistory = messages.filter(msg => msg.role === 'user' ||
        (msg.role === 'assistant' && !msg.content.includes('안녕하세요! AI 분석 도우미입니다')));

      await sendAgentMessageStream(
        messageToSend,
        conversationHistory,
        // onToken: 토큰이 올 때마다 호출
        (token, fullText) => {
          setStreamingText(fullText);
        },
        // onStatus: 상태 업데이트
        (status) => {
          setStatusText(status);
        },
        // onComplete: 완료 시 호출
        (response) => {
          const assistantMessage = {
            role: 'assistant',
            content: response.natural_language || '응답을 받지 못했습니다.',
            blocks: response.blocks || [],
            intent: response.intent,
            sqlQuery: response.sql_query,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, assistantMessage]);
          setStreamingText('');
          setStatusText('');
          setIsLoading(false);
        }
      );
    } catch (error) {
      console.error('AI 메시지 처리 실패:', error);

      const errorMessage = {
        role: 'assistant',
        content: '죄송합니다. 메시지를 처리하는 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
        timestamp: new Date(),
        isError: true
      };

      setMessages(prev => [...prev, errorMessage]);
      setError(error.message);
      setStreamingText('');
      setStatusText('');
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const formatTime = (date) => {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // 부서별 추천 질문 가져오기
  const getSuggestedQuestions = () => {
    const user = getStoredUser();
    const department = user?.department || '';

    // 품질관리팀 추천 질문
    if (department.includes('품질') || department.includes('QC') || department.includes('QA')) {
      return [
        '최근 불량률 추이를 선그래프로 보여줘',
        '오늘 품질 알람이 가장 많이 발생한 설비는?',
        '사출 1호기의 최근 온도 변화에 대해서 분석해줘',
        '최근 7일간 설비별 불량 발생 현황을 막대그래프로 보여줘'
      ];
    }

    // 생산관리팀 추천 질문
    if (department.includes('생산') || department.includes('제조')) {
      return [
        '현재 가동 중인 설비 현황은?',
        '오늘 생산량이 가장 많은 설비는?',
        '최근 7일간 생산량 추이를 선그래프로 보여줘',
        '설비별 가동률을 비교해줘'
      ];
    }

    // 설비/유지보수팀 추천 질문
    if (department.includes('설비') || department.includes('유지보수') || department.includes('보전')) {
      return [
        '오늘 알람이 가장 많이 발생한 설비는?',
        '최근 7일간 설비별 알람 발생 추이를 보여줘',
        '사출 1호기의 최근 사이클 타임 변화를 분석해줘',
        '현재 비가동 설비 현황은?'
      ];
    }

    // 기본 추천 질문 (부서 정보 없거나 기타 부서)
    return [
      '현재 가동 중인 설비 현황은?',
      '오늘 알람이 가장 많이 발생한 설비는?',
      '최근 7일간 생산량 추이를 선그래프로 보여줘',
      '사출 1호기에 최근 샷 온도 변화에 대해서 분석해줘'
    ];
  };

  const suggestedQuestions = getSuggestedQuestions();

  const handleSuggestionClick = (question) => {
    setInputValue(question);
    textareaRef.current?.focus();
  };

  // ContentBlock 렌더링 함수
  const renderContentBlock = (block, index) => {
    const { type, content } = block;

    switch (type) {
      case 'table':
        return <TableRenderer key={index} data={content} />;

      case 'chart':
        return (
          <div key={index} className="chart-container">
            <DynamicChart config={content} />
          </div>
        );

      case 'text':
        return (
          <div key={index} className="response-text">
            {content}
          </div>
        );

      default:
        return (
          <div key={index} className="response-text">
            {JSON.stringify(content)}
          </div>
        );
    }
  };

  return (
    <MainLayout>
      <div className="ai-analysis-page">
        {/* 챗봇 본문 영역 */}
        <div className="ai-chat-container">
          <div className="ai-chat-wrapper">
            {/* 챗봇 헤더 */}
            <div className="ai-chat-header">
              <span className="material-symbols-outlined">smart_toy</span>
              <span className="ai-chat-title">AI 분석</span>
            </div>

            {/* 메시지 영역 */}
            <div className="ai-messages-container">
              {messages.map((message, index) => (
                <div key={index} className={`ai-message ${message.role}`}>
                  <div className="ai-message-avatar">
                    {message.role === 'assistant' ? (
                      <span className="material-symbols-outlined">smart_toy</span>
                    ) : (
                      <span className="material-symbols-outlined">person</span>
                    )}
                  </div>
                  <div className="ai-message-content">
                    <div className="ai-message-header">
                      <span className="ai-message-role">
                        {message.role === 'assistant' ? 'AI 어시스턴트' : '사용자'}
                      </span>
                      <span className="ai-message-time">{formatTime(message.timestamp)}</span>
                    </div>
                    <div className={`ai-message-text ${message.isError ? 'error' : ''}`}>
                      {/* 자연어 답변 */}
                      {message.content && (
                        <div className="response-text">
                          {message.content.replace(/<table[\s\S]*?<\/table>/gi, '').trim()}
                        </div>
                      )}

                      {/* ContentBlock 렌더링 */}
                      {message.blocks && message.blocks.map((block, idx) => renderContentBlock(block, idx))}

                      {/* SQL 쿼리 (디버그용, 필요시 표시) */}
                      {message.sqlQuery && false && (  // false로 숨김 처리
                        <details className="sql-details">
                          <summary>SQL 쿼리 보기</summary>
                          <pre className="sql-query">
                            <code>{message.sqlQuery}</code>
                          </pre>
                        </details>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {isLoading && (
                <div className="ai-message assistant">
                  <div className="ai-message-avatar">
                    <span className="material-symbols-outlined">smart_toy</span>
                  </div>
                  <div className="ai-message-content">
                    <div className="ai-message-header">
                      <span className="ai-message-role">AI 어시스턴트</span>
                      {statusText && <span className="ai-status-text">{statusText}</span>}
                    </div>
                    {streamingText ? (
                      <div className="ai-message-text">
                        <div className="response-text streaming">
                          {streamingText}
                          <span className="streaming-cursor">▌</span>
                        </div>
                      </div>
                    ) : (
                      <div className="ai-message-loading">
                        <span className="ai-loading-dot"></span>
                        <span className="ai-loading-dot"></span>
                        <span className="ai-loading-dot"></span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* 추천 질문 - 로딩 중이 아닐 때 항상 표시 */}
            {!isLoading && (
              <div className="ai-suggestions">
                <div className="ai-suggestions-title">추천 질문</div>
                <div className="ai-suggestions-list">
                  {suggestedQuestions.map((question, index) => (
                    <button
                      key={index}
                      className="ai-suggestion-item"
                      onClick={() => handleSuggestionClick(question)}
                    >
                      <span className="material-symbols-outlined">lightbulb</span>
                      {question}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* 입력 영역 */}
            <div className="ai-input-container">
              <form onSubmit={handleSubmit} className="ai-input-form">
                <textarea
                  ref={textareaRef}
                  className="ai-input-textarea"
                  placeholder="질문을 입력하세요... (Shift+Enter로 줄바꿈)"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={1}
                  disabled={isLoading}
                />
                <button
                  type="submit"
                  className="ai-input-submit"
                  disabled={!inputValue.trim() || isLoading}
                >
                  <span className="material-symbols-outlined">send</span>
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}

export default AIAnalysis;
